import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
    ArrowLeft,
    Wallet,
    IndianRupee,
    CheckCircle2,
} from "lucide-react";

import { Header } from "./Header";
import { MobileHeader } from "./MobileHeader";

import { useCreateOrder } from "../hooks/useCreateOrder";
import { useVerifyPayment } from "../hooks/useVerifyPayment";
import { useWalletDetails } from "../hooks/useWalletDetails";
import { useUpdateAutoTopupSettings } from "../hooks/useUpdateAutoTopupSettings";
import { useDisableAutoTopup } from "../hooks/useDisableAutoTopup";

import { useCreateAuthorizationTransaction } from "../hooks/useCreateAuthorizationTransaction";
import { useVerifyAuthorizationTransaction } from "../hooks/useVerifyAuthorizationTransaction";

const PRESET_AMOUNTS = [

    500,

    1000,

    2500,

    5000,

];

export function TopUpWallet() {

    const [autoPayEnabled, setAutoPayEnabled] =
        useState(false);

    const [threshold, setThreshold] =
        useState(500);

    const [topupAmount, setTopupAmount] =
        useState(5000);

    const navigate = useNavigate();

    const outletUid =
        localStorage.getItem(
            "selectedOutletUid"
        );

    const {
        data,
        isLoading,
        isError,
        error,
    } = useWalletDetails(

        outletUid

    );

    useEffect(() => {

        if (!data) {

            return;

        }

        //--------------------------------------------------
        // Wallet
        //--------------------------------------------------

        setWalletBalance(

            Number(
                data.walletBalance || 0
            )

        );

        //--------------------------------------------------
        // Auto-Pay
        //--------------------------------------------------

        const autoTopup =
            data.autoTopup || {};

        setAutoPayEnabled(

            autoTopup.enabled ?? false

        );

        setThreshold(

            Number(
                autoTopup.threshold ?? 500
            )

        );

        setTopupAmount(

            Number(
                autoTopup.topupAmount ?? 5000
            )

        );

        setAuthorizedMaxAmount(
            Number(
                autoTopup.authorizedMaxAmount ?? 5000
            )
        );

    }, [data]);

    const createOrderMutation =
        useCreateOrder();

    const verifyPaymentMutation =
        useVerifyPayment();

    const createAuthorizationTransactionMutation =
        useCreateAuthorizationTransaction();

    const verifyAuthorizationTransactionMutation =
        useVerifyAuthorizationTransaction();

    const updateAutoTopupSettingsMutation =
        useUpdateAutoTopupSettings();

    const disableAutoTopupMutation =
        useDisableAutoTopup();

    const [selectedAmount, setSelectedAmount] =
        useState(500);

    const [customAmount, setCustomAmount] =
        useState("");

    const [walletBalance, setWalletBalance] =
        useState(0);

    const [authorizedMaxAmount, setAuthorizedMaxAmount] = useState(5000);

    const [updatingThreshold, setUpdatingThreshold] = useState(false);

    const [updatingTopupAmount, setUpdatingTopupAmount] = useState(false);

    //--------------------------------------------------
    // Amount
    //--------------------------------------------------

    const amount = useMemo(() => {

        if (

            customAmount !== "" &&

            Number(customAmount) > 0

        ) {

            return Number(customAmount);

        }

        return selectedAmount;

    }, [

        selectedAmount,

        customAmount,

    ]);

    //--------------------------------------------------
    // Helpers
    //--------------------------------------------------

    function selectPresetAmount(value) {

        setSelectedAmount(value);

        setCustomAmount("");

    }

    function handleCustomAmount(event) {

        setCustomAmount(event.target.value);

    }

    async function handleThresholdChange(newThreshold) {

        const previousThreshold =
            threshold;

        setThreshold(
            newThreshold
        );

        if (!autoPayEnabled) {

            return;

        }

        setUpdatingThreshold(true);

        try {

            await updateAutoTopupSettingsMutation
                .mutateAsync({

                    outletUid,

                    threshold:
                        newThreshold,

                    topupAmount,

                });

            alert(
                "Auto-Pay threshold updated successfully."
            );

        }

        catch (error) {

            setThreshold(
                previousThreshold
            );

            alert(

                error.response?.data?.error ||

                error.message

            );

        }

        finally {

            setUpdatingThreshold(false);

        }

    }

    async function handleTopupAmountChange(
        newTopupAmount
    ) {

        //--------------------------------------------------
        // Auto-Pay is OFF
        //--------------------------------------------------

        if (!autoPayEnabled) {

            setTopupAmount(
                newTopupAmount
            );

            return;

        }

        //--------------------------------------------------
        // New amount exceeds authorization
        //--------------------------------------------------

        if (
            newTopupAmount >
            authorizedMaxAmount
        ) {

            alert(

                `Your current Auto-Pay authorization allows up to ₹${authorizedMaxAmount.toLocaleString("en-IN")}. Please re-authorize Auto-Pay to increase this amount. You can re-authorize by disabling and re-enabling Auto-Pay.`

            );

            return;

        }

        //--------------------------------------------------
        // Save new amount
        //--------------------------------------------------

        const previousTopupAmount =
            topupAmount;

        setTopupAmount(
            newTopupAmount
        );

        setUpdatingTopupAmount(true);

        try {

            await updateAutoTopupSettingsMutation
                .mutateAsync({

                    outletUid,

                    threshold,

                    topupAmount:
                        newTopupAmount,

                });

            alert(
                "Auto-Pay top-up amount updated successfully."
            );

        }

        catch (error) {

            setTopupAmount(
                previousTopupAmount
            );

            alert(

                error.response?.data?.error ||

                error.message

            );

        }

        finally {

            setUpdatingTopupAmount(false);

        }


    }

    //--------------------------------------------------
    // Payment
    //--------------------------------------------------

    async function handlePayment() {

        try {

            const outletUid =
                localStorage.getItem(
                    "selectedOutletUid"
                );

            if (!outletUid) {

                alert(
                    "Please select an outlet first."
                );

                return;

            }

            //--------------------------------------------------
            // Create Razorpay Order
            //--------------------------------------------------

            const order =
                await createOrderMutation.mutateAsync({

                    outletUid,

                    amount,

                });

            //--------------------------------------------------
            // Open Razorpay Checkout
            //--------------------------------------------------

            console.log("Razorpay Order: ", order);

            const razorpay = new window.Razorpay({

                key: order.key,

                amount: order.amount,

                currency: order.currency,

                order_id: order.orderId,

                name: "NextGo",

                description: "Wallet Top-Up",

                theme: {

                    color: "#2162ad",

                },

                handler: async function (response) {

                    try {

                        await verifyPaymentMutation.mutateAsync({

                            razorpayPaymentId:

                                response.razorpay_payment_id,

                            razorpayOrderId:

                                response.razorpay_order_id,

                            razorpaySignature:

                                response.razorpay_signature,

                        });

                        alert(

                            "Wallet topped up successfully."

                        );

                        navigate("/wallet");

                    }

                    catch (error) {

                        alert(

                            error.response?.data?.error ||

                            error.message

                        );

                    }

                },

                modal: {

                    ondismiss: function () {

                        console.log(

                            "Payment cancelled."

                        );

                    },

                },

            });

            razorpay.open();

        }

        catch (error) {

            alert(

                error.response?.data?.error ||

                error.message

            );

        }

    }

    async function handleEnableAutoPay(enabled) {

        if (!enabled) {

            try {

                const outletUid =
                    localStorage.getItem(
                        "selectedOutletUid"
                    );

                if (!outletUid) {

                    alert(
                        "Please select an outlet first."
                    );

                    return;

                }

                await disableAutoTopupMutation
                    .mutateAsync(

                        outletUid

                    );

                setAutoPayEnabled(false);

                alert(
                    "Auto-Pay disabled successfully."
                );

            }

            catch (error) {

                // Keep switch ON because
                // disabling failed.

                setAutoPayEnabled(true);

                alert(

                    error.response?.data?.error ||

                    error.message

                );

            }

            return;

        }

        try {

            const outletUid =
                localStorage.getItem(
                    "selectedOutletUid"
                );

            if (!outletUid) {

                alert(
                    "Please select an outlet first."
                );

                return;

            }

            //--------------------------------------------------
            // Create Authorization Transaction
            //--------------------------------------------------

            const transaction =
                await createAuthorizationTransactionMutation.mutateAsync({

                    outletUid,

                    threshold,

                    topupAmount,

                });

            //--------------------------------------------------
            // Open Razorpay Checkout
            //--------------------------------------------------

            const razorpay =
                new window.Razorpay({

                    key:
                        transaction.key,

                    customer_id:
                        transaction.customerId,

                    order_id:
                        transaction.orderId,

                    recurring:
                        true,

                    name:
                        "NextGo",

                    description:
                        "Enable Auto-Pay",

                    theme: {

                        color:
                            "#2162ad",

                    },

                    handler: async function (response) {

                        try {

                            await verifyAuthorizationTransactionMutation.mutateAsync({

                                outletUid,

                                threshold,

                                topupAmount,

                                razorpayPaymentId:
                                    response.razorpay_payment_id,

                                razorpayOrderId:
                                    response.razorpay_order_id,

                                razorpaySignature:
                                    response.razorpay_signature,

                            });

                            setAutoPayEnabled(true);

                            alert(
                                "Auto-Pay enabled successfully."
                            );

                        }

                        catch (error) {

                            alert(

                                error.response?.data?.error ||

                                error.message

                            );

                        }

                    },

                    modal: {

                        ondismiss() {

                            setAutoPayEnabled(false);

                        },

                    },

                });

            razorpay.open();

        }

        catch (error) {

            alert(

                error.response?.data?.error ||

                error.message

            );

        }

    }

    return (

        <>

            <MobileHeader />

            <main className="topup-wallet-page">

                <Header

                    title="Top Up Wallet"

                    subtitle="Add funds to your wallet to redeem offers and grow your business."

                />

                <button

                    className="back-button"

                    onClick={() => navigate(-1)}

                >

                    <ArrowLeft size={18} />

                    Back

                </button>

                <div className="topup-wallet-layout">

                    {/* LEFT COLUMN */}

                    <section className="topup-wallet-main">

                        {/* Wallet Balance */}

                        <div className="topup-wallet-card">

                            <div className="topup-wallet-balance">

                                <div>

                                    <span className="topup-wallet-label">

                                        Wallet Balance

                                    </span>

                                    <h2>

                                        ₹{walletBalance.toLocaleString("en-IN", {

                                            minimumFractionDigits: 2,

                                        })}

                                    </h2>

                                </div>

                                <div className="topup-wallet-icon">

                                    <Wallet size={34} />

                                </div>

                            </div>

                        </div>

                        {/* Amount Selection */}

                        <div className="topup-wallet-card">

                            <h3>

                                1. Select Top-up Amount

                            </h3>

                            <div className="topup-amount-grid">

                                {

                                    PRESET_AMOUNTS.map((value) => (

                                        <button

                                            key={value}

                                            className={

                                                amount === value &&

                                                    customAmount === ""

                                                    ?

                                                    "topup-amount active"

                                                    :

                                                    "topup-amount"

                                            }

                                            onClick={() =>

                                                selectPresetAmount(

                                                    value

                                                )

                                            }

                                        >

                                            {

                                                amount === value &&

                                                customAmount === "" && (

                                                    <div className="topup-selected">

                                                        <CheckCircle2

                                                            size={18}

                                                        />

                                                    </div>

                                                )

                                            }

                                            <h4>

                                                ₹{value.toLocaleString()}

                                            </h4>

                                            <span>

                                                {

                                                    value === 500

                                                        ?

                                                        "Quick top-up"

                                                        :

                                                        value === 1000

                                                            ?

                                                            "Popular"

                                                            :

                                                            value === 2500

                                                                ?

                                                                "Great choice"

                                                                :

                                                                "Best value"

                                                }

                                            </span>

                                        </button>

                                    ))

                                }

                            </div>

                            {/* Custom */}

                            <div className="topup-custom">

                                <div>

                                    <h4>

                                        Custom Amount

                                    </h4>

                                    <span>

                                        Enter any amount (minimum ₹100)

                                    </span>

                                </div>

                                <div className="input-with-prefix topup-input-container">

                                    <IndianRupee

                                        size={18}

                                    />

                                    <input
                                        className="topup-input"

                                        type="number"

                                        placeholder="Enter amount"

                                        min="100"

                                        value={customAmount}

                                        onChange={handleCustomAmount}

                                    />

                                </div>

                            </div>

                        </div>

                        {/* Payment Summary */}

                        <div className="topup-wallet-card">

                            <h3>

                                2. Top-up Summary

                            </h3>

                            <div className="topup-summary">

                                <div className="topup-summary-row">

                                    <span>

                                        Top-up Amount

                                    </span>

                                    <strong>

                                        ₹{amount.toLocaleString("en-IN", {

                                            minimumFractionDigits: 2,

                                        })}

                                    </strong>

                                </div>

                                <div className="topup-summary-row">

                                    <span>

                                        Processing Fee

                                    </span>

                                    <strong>

                                        ₹0.00

                                    </strong>

                                </div>

                                <div className="topup-summary-divider" />

                                <div className="topup-summary-row total">

                                    <span>

                                        Total Amount

                                    </span>

                                    <strong>

                                        ₹{amount.toLocaleString("en-IN", {

                                            minimumFractionDigits: 2,

                                        })}

                                    </strong>

                                </div>

                            </div>

                            <div className="topup-secure-text">

                                Secure payments powered by Razorpay

                            </div>

                            <button

                                className="primary-btn topup-payment-btn"

                                disabled={

                                    createOrderMutation.isPending ||

                                    amount < 100

                                }

                                onClick={handlePayment}

                            >

                                {

                                    createOrderMutation.isPending

                                        ?

                                        "Processing..."

                                        :

                                        "Proceed to Payment"

                                }

                            </button>

                            <p className="topup-payment-note">

                                You will be redirected to Razorpay to complete
                                your payment securely.

                            </p>

                        </div>

                    </section>

                    {/* RIGHT COLUMN */}

                    <aside className="topup-wallet-sidebar">

                        {/* Auto Pay */}

                        <div className="topup-wallet-card">

                            <div className="topup-sidebar-title">

                                <h3>

                                    Setup Auto-Pay

                                </h3>

                                <span className="recommended-badge">

                                    Recommended

                                </span>

                            </div>

                            <p className="topup-sidebar-description">

                                Never run out of balance. Auto-Pay
                                automatically tops up your wallet whenever your
                                balance falls below your selected threshold.

                            </p>

                            <div className="autopay-option">

                                <span>

                                    Enable Auto-Pay

                                </span>

                                <label className="autopay-switch">

                                    <input

                                        type="checkbox"

                                        checked={autoPayEnabled}

                                        onChange={(e) =>

                                            handleEnableAutoPay(

                                                e.target.checked

                                            )

                                        }

                                    />

                                    <span className="autopay-slider" />

                                </label>

                            </div>

                            <div className="autopay-field">

                                <label>

                                    When balance goes below

                                </label>

                                <select

                                    value={threshold}

                                    disabled={updatingThreshold}

                                    onChange={(e) =>
                                        handleThresholdChange(
                                            Number(e.target.value)
                                        )
                                    }

                                >

                                    <option value={500}>
                                        ₹500
                                    </option>

                                    <option value={1000}>
                                        ₹1,000
                                    </option>

                                    <option value={2500}>
                                        ₹2,500
                                    </option>

                                    <option value={5000}>
                                        ₹5,000
                                    </option>

                                </select>
                            </div>

                            <div className="autopay-field">

                                <label>

                                    Auto top-up amount

                                </label>

                                <select

                                    value={topupAmount}

                                    disabled={updatingTopupAmount}

                                    onChange={(e) =>
                                        handleTopupAmountChange(
                                            Number(e.target.value)
                                        )
                                    }

                                >

                                    <option value={1000}>
                                        ₹1,000
                                    </option>

                                    <option value={2500}>
                                        ₹2,500
                                    </option>

                                    <option value={5000}>
                                        ₹5,000
                                    </option>

                                    <option value={10000}>
                                        ₹10,000
                                    </option>

                                </select>
                            </div>

                            <div className="autopay-info">

                                You can change these settings anytime from
                                Wallet Settings.

                            </div>

                        </div>

                        {/* Benefits */}

                        <div className="topup-wallet-card">

                            <h3>

                                Why keep your wallet topped up?

                            </h3>

                            <div className="wallet-benefits">

                                <div className="wallet-benefit">

                                    <div className="wallet-benefit-icon green">

                                        ✓

                                    </div>

                                    <div>

                                        <h4>

                                            Never miss a customer

                                        </h4>

                                        <p>

                                            Reach customers wherever they make a purchase.

                                        </p>

                                    </div>

                                </div>

                                <div className="wallet-benefit">

                                    <div className="wallet-benefit-icon orange">

                                        ↗

                                    </div>

                                    <div>

                                        <h4>

                                            Grow your business

                                        </h4>

                                        <p>

                                            Run more campaigns and acquire new customers.

                                        </p>

                                    </div>

                                </div>

                                <div className="wallet-benefit">

                                    <div className="wallet-benefit-icon purple">

                                        ⏱

                                    </div>

                                    <div>

                                        <h4>

                                            Save time

                                        </h4>

                                        <p>

                                            With Auto-Pay save time on top-up and focus on growing.

                                        </p>

                                    </div>

                                </div>

                            </div>

                        </div>

                    </aside>

                </div>

            </main>

        </>

    );

}